import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { getPipeSize } from '@/utils';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { useGetStructureId } from '@/hooks/useGetStructureId';

export const useGetDetailsData = (assemblyDetails: Record<string, string> | undefined) => {
  const unitSystem = useAtomValue(state.unitSystem);
  const { getUnitSuffix } = useUnitConversion();
  const structureId = useGetStructureId();
  const structurePipesByStructureId = useAtomValue(state.structurePipesByStructureId);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);

  const displayableKeys: Set<string> = useMemo(() => {
    const reviewPanelConfig = assemblyTemplate?.reviewPanelConfig ?? [];

    const allFieldKeys = reviewPanelConfig
      .flatMap((group) => group.groupSection.flatMap((section) => section?.fieldKeys ?? []))
      .filter((key): key is string => typeof key === 'string');

    return new Set(allFieldKeys);
  }, [assemblyTemplate?.reviewPanelConfig]);

  const reviewSections = useMemo(() => {
    const reviewPanelConfig = assemblyTemplate?.reviewPanelConfig ?? [];
    if (!assemblyDetails || !reviewPanelConfig) return [];

    const displayableEntries = Object.entries(assemblyDetails).filter(([key]) =>
      displayableKeys.has(key)
    );

    const pair = (k: string, v: string | number): [string, string | number] => [k, v];

    return reviewPanelConfig.map((group) => {
      const sections = (group.groupSection ?? [])
        .map((section) => {
          const keys =
            Array.isArray(section?.fieldKeys) && section.fieldKeys.length
              ? section.fieldKeys
              : displayableEntries.map(([k]) => k);
          const data = keys.reduce<[string, string | number][]>((accumulator, key) => {
            if (key && displayableKeys.has(key) && Object.hasOwn(assemblyDetails, key)) {
              let value = assemblyDetails[key];
              if (key === 'size') {
                value = getPipeSize({
                  size: Number(assemblyDetails[key]),
                  getUnitSuffix,
                  unitSystem,
                  displayUnit: false,
                  structureSteelPipes: structurePipesByStructureId[structureId],
                }) as string;
              }
              if (typeof value === 'string' || typeof value === 'number') {
                accumulator.push(pair(key, value));
              } else {
                accumulator.push(pair(key, 'N/A'));
              }
            }
            return accumulator;
          }, []);

          return { title: section?.title ?? '', data };
        })
        .filter((s) => s.data.length > 0);

      return { groupName: group.groupName ?? '', sections };
    });
  }, [
    assemblyDetails,
    assemblyTemplate?.reviewPanelConfig,
    displayableKeys,
    getUnitSuffix,
    structureId,
    structurePipesByStructureId,
    unitSystem,
  ]);

  return { reviewSections };
};
